import React, { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Goal {
  id: string;
  title: string;
  description: string;
}

interface GoalGroup {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  goals: Goal[];
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const goalGroups: GoalGroup[] = [
  {
    id: 'variant-identity',
    title: 'Variant Identity',
    description:
      'Establish the variant data model, lifecycle, and coverage so every product has a well-defined visual identity.',
    icon: <FlagOutlinedIcon fontSize="small" />,
    goals: [
      {
        id: 'G1',
        title: 'Chain-scoped variant system',
        description:
          'Every front crop belongs to one active variant. Correct lifecycle: merge within UPC, split mixed packaging, auto-deactivate on zero crops. Representative invariant enforced at write time.',
      },
      {
        id: 'G2',
        title: 'Bootstrap and enrich variant coverage',
        description:
          'Enrichment tasks for under-covered variants. FD classification for false detection review. Target: every active planogram UPC has variant coverage.',
      },
    ],
  },
  {
    id: 'labeling-workflow',
    title: 'Labeling Workflow',
    description:
      'A queue-driven task system that routes work to labelers with a fast, keyboard-first task player.',
    icon: <FlagOutlinedIcon fontSize="small" />,
    goals: [
      {
        id: 'G3',
        title: 'Non-overlapping task system',
        description:
          'Queue-driven with Redis + MySQL. No two active tasks share an entity. Auto-regeneration when queues drain. Precondition checks at submit for external changes only.',
      },
      {
        id: 'G4',
        title: 'Fast, keyboard-first task player',
        description:
          'Sub-200ms task transitions. Keyboard shortcuts for all actions. Lease timer visible. Zero-click happy path for common outcomes.',
      },
      {
        id: 'G5',
        title: 'Core task types',
        description:
          'variant_review, variant_curator, uncertain_classification, fd_classification — each with a tailored UI. Unassigned crop assignment is handled via variant_review highlights. FD classification is event-driven (ingressed on FD creation).',
      },
    ],
  },
  {
    id: 'data-quality',
    title: 'Data Quality',
    description:
      'Every mutation is traceable and the system catches errors early through human review gates.',
    icon: <FlagOutlinedIcon fontSize="small" />,
    goals: [
      {
        id: 'G6',
        title: 'Full mutation audit trail',
        description:
          'Every variant/crop change logged with actor, action, before/after state, and linked task_id.',
      },
      {
        id: 'G7',
        title: 'Human review gates',
        description:
          'Pending crops require accept/reject before entering the dataset. QA sampling on submitted tasks. Outlier detection via centroid distance.',
      },
    ],
  },
  {
    id: 'admin-ops',
    title: 'Admin & Ops',
    description:
      'Admins can monitor queue health, manage labelers, and investigate variants.',
    icon: <FlagOutlinedIcon fontSize="small" />,
    goals: [
      {
        id: 'G8',
        title: 'Labeling dashboard',
        description:
          'Queue health, labeler performance, coverage metrics. Mock data in v1, real data post-MVP.',
      },
      {
        id: 'G9',
        title: 'Admin controls',
        description:
          'Queue enable/disable per chain. Labeler queue assignments. On-demand regeneration. Variant browser for investigation.',
      },
    ],
  },
];

const nonGoals: { id: string; title: string; reason: string }[] = [
  {
    id: 'NG1',
    title: 'Refactor normalized_upc into UPC-groups',
    reason: 'Large schema change across the entire system. Not needed for v1 — we work within the existing UPC model.',
  },
  {
    id: 'NG2',
    title: 'Cross-chain variants',
    reason: 'Variants are chain-scoped in v1. Cross-chain dedup adds significant complexity for unclear benefit.',
  },
  {
    id: 'NG3',
    title: 'Automatic cohesion-based split detection',
    reason: 'Manual split is supported within the Variant Curator. Auto-detection of when to split is deferred — requires more research.',
  },
  {
    id: 'NG4',
    title: 'AI audit / planogram compliance gate',
    reason: 'Separate module with its own scope. Not related to variant curation.',
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export const GoalsPage: React.FC = () => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(goalGroups.map((g) => g.id))
  );
  const [showNonGoals, setShowNonGoals] = useState(true);

  const toggleGroup = (id: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totalGoals = goalGroups.reduce((sum, g) => sum + g.goals.length, 0);

  return (
    <div
      style={{
        maxWidth: 900,
        margin: '0 auto',
        padding: '32px 24px',
        fontFamily: 'var(--font-sans)',
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1
          style={{
            fontSize: 22,
            fontWeight: 600,
            color: 'var(--text-primary)',
            margin: 0,
            marginBottom: 8,
          }}
        >
          Goals
        </h1>
        <p
          style={{
            fontSize: 13,
            color: 'var(--text-secondary)',
            margin: 0,
            lineHeight: 1.5,
          }}
        >
          What we're trying to achieve with the Forge module. Each goal has a
          priority (P0 = must have, P1 = should have, P2 = nice to have) and
          maps to specific tasks in the Task List.
        </p>
      </div>

      {/* Summary bar */}
      <div
        style={{
          display: 'flex',
          gap: 16,
          marginBottom: 24,
          padding: '12px 16px',
          background: 'var(--surface)',
          borderRadius: 8,
          border: '1px solid var(--border)',
        }}
      >
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          <strong style={{ color: 'var(--text-primary)' }}>{totalGoals}</strong>{' '}
          goals across {goalGroups.length} areas
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          <strong style={{ color: 'var(--text-primary)' }}>{nonGoals.length}</strong>{' '}
          non-goals
        </div>
      </div>

      {/* Goal groups */}
      {goalGroups.map((group) => {
        const isExpanded = expandedGroups.has(group.id);
        return (
          <div
            key={group.id}
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              marginBottom: 12,
              overflow: 'hidden',
            }}
          >
            {/* Group header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '14px 16px',
                cursor: 'pointer',
                userSelect: 'none',
              }}
              onClick={() => toggleGroup(group.id)}
            >
              <span style={{ color: 'var(--text-secondary)', display: 'flex' }}>
                {group.icon}
              </span>
              <span
                style={{
                  flex: 1,
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                }}
              >
                {group.title}
              </span>
              <span
                style={{
                  fontSize: 11,
                  color: 'var(--text-tertiary)',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                {group.goals.length} goals
              </span>
              {isExpanded ? (
                <ExpandLessIcon
                  style={{ fontSize: 18, color: 'var(--text-tertiary)' }}
                />
              ) : (
                <ExpandMoreIcon
                  style={{ fontSize: 18, color: 'var(--text-tertiary)' }}
                />
              )}
            </div>

            {/* Group description */}
            {isExpanded && (
              <>
                <div
                  style={{
                    padding: '0 16px 10px',
                    fontSize: 12,
                    color: 'var(--text-tertiary)',
                    borderBottom: '1px solid var(--divider)',
                  }}
                >
                  {group.description}
                </div>

                {/* Goals */}
                {group.goals.map((goal, idx) => (
                  <div
                    key={goal.id}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      padding: '10px 16px',
                      borderBottom:
                        idx < group.goals.length - 1
                          ? '1px solid var(--divider)'
                          : 'none',
                      transition: 'background-color 80ms ease',
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        'var(--sidebar-hover)')
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = 'transparent')
                    }
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          marginBottom: 3,
                        }}
                      >
                        <span
                          style={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: 11,
                            color: 'var(--text-tertiary)',
                            minWidth: 24,
                          }}
                        >
                          {goal.id}
                        </span>
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 500,
                            color: 'var(--text-primary)',
                          }}
                        >
                          {goal.title}
                        </span>
                      </div>
                      <div
                        style={{
                          paddingLeft: 32,
                          fontSize: 12,
                          lineHeight: 1.5,
                          color: 'var(--text-secondary)',
                        }}
                      >
                        {goal.description}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        );
      })}

      {/* Non-goals */}
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          marginTop: 24,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 16px',
            cursor: 'pointer',
            userSelect: 'none',
          }}
          onClick={() => setShowNonGoals((v) => !v)}
        >
          <span style={{ color: 'var(--text-secondary)', display: 'flex' }}>
            <BlockOutlinedIcon fontSize="small" />
          </span>
          <span
            style={{
              flex: 1,
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--text-primary)',
            }}
          >
            Non-Goals (v1)
          </span>
          <span
            style={{
              fontSize: 11,
              color: 'var(--text-tertiary)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            {nonGoals.length} items
          </span>
          {showNonGoals ? (
            <ExpandLessIcon
              style={{ fontSize: 18, color: 'var(--text-tertiary)' }}
            />
          ) : (
            <ExpandMoreIcon
              style={{ fontSize: 18, color: 'var(--text-tertiary)' }}
            />
          )}
        </div>

        {showNonGoals && (
          <>
            <div
              style={{
                padding: '0 16px 10px',
                fontSize: 12,
                color: 'var(--text-tertiary)',
                borderBottom: '1px solid var(--divider)',
              }}
            >
              Explicitly out of scope for v1. Listed here so we don't
              accidentally creep into them.
            </div>

            {nonGoals.map((ng, idx) => (
              <div
                key={ng.id}
                style={{
                  padding: '10px 16px',
                  borderBottom:
                    idx < nonGoals.length - 1
                      ? '1px solid var(--divider)'
                      : 'none',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 3,
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 11,
                      color: 'var(--text-tertiary)',
                      minWidth: 30,
                    }}
                  >
                    {ng.id}
                  </span>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: 'var(--text-primary)',
                      textDecoration: 'line-through',
                      opacity: 0.7,
                    }}
                  >
                    {ng.title}
                  </span>
                </div>
                <div
                  style={{
                    paddingLeft: 38,
                    fontSize: 12,
                    color: 'var(--text-tertiary)',
                    fontStyle: 'italic',
                  }}
                >
                  {ng.reason}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};
